/**
 * Hosts the proxy must not fetch.
 * This is a curated suffix list plus government endings, not a directory of every site.
 * A local bank absent from the list can still be added later.
 */

export const RISK_MESSAGE =
  "Banks, payment sites, mail logins, and government sites can't be opened here. Try a normal web page.";

/** Matched as the whole host or as a subdomain. www is included by the dot rule. */
const SUFFIX: readonly string[] = [
  // Reported credit union, then banks and card networks people phish.
  "ent.com",
  "chase.com",
  "jpmorgan.com",
  "jpmorganchase.com",
  "bankofamerica.com",
  "bofa.com",
  "wellsfargo.com",
  "citi.com",
  "citibank.com",
  "capitalone.com",
  "usbank.com",
  "pnc.com",
  "truist.com",
  "regions.com",
  "fifththird.com",
  "53.com",
  "key.com",
  "huntington.com",
  "citizensbank.com",
  "ally.com",
  "discover.com",
  "americanexpress.com",
  "amex.com",
  "visa.com",
  "mastercard.com",
  "hsbc.com",
  "hsbc.co.uk",
  "barclays.com",
  "barclays.co.uk",
  "lloydsbank.com",
  "natwest.com",
  "santander.com",
  "santander.co.uk",
  "santanderbank.com",
  "bbva.com",
  "bnpparibas.com",
  "societegenerale.com",
  "credit-agricole.fr",
  "deutsche-bank.de",
  "db.com",
  "commerzbank.com",
  "ing.com",
  "ing.nl",
  "abnamro.nl",
  "rabobank.com",
  "rabobank.nl",
  "ubs.com",
  "credit-suisse.com",
  "goldmansachs.com",
  "morganstanley.com",
  "td.com",
  "rbc.com",
  "scotiabank.com",
  "bmo.com",
  "cibc.com",
  "navyfederal.org",
  "penfed.org",
  "usaa.com",
  "becu.org",
  "nfcu.org",
  "golden1.com",
  "patelco.org",
  "vystarcu.org",
  "ncsecu.org",
  "dcu.org",
  "americafirst.com",
  "firsttechfed.com",
  "logixbanking.com",
  "schwab.com",
  "fidelity.com",
  "vanguard.com",
  "etrade.com",
  "tdameritrade.com",
  "interactivebrokers.com",
  "sofi.com",
  "chime.com",
  "revolut.com",
  "wise.com",
  "n26.com",
  "monzo.com",
  "starlingbank.com",
  "standardchartered.com",
  "icbc.com.cn",
  "ccb.com",
  "boc.cn",
  "abchina.com",
  "cmbchina.com",
  "citicbank.com",
  "mufg.jp",
  "smbc.co.jp",
  "mizuhobank.co.jp",
  "anz.com",
  "westpac.com.au",
  "commbank.com.au",
  "nab.com.au",
  "macquarie.com",
  // Payment.
  "paypal.com",
  "paypal.me",
  "venmo.com",
  "stripe.com",
  "squareup.com",
  "cash.app",
  "skrill.com",
  "neteller.com",
  "payoneer.com",
  "klarna.com",
  "affirm.com",
  "afterpay.com",
  "clearpay.com",
  "zelle.com",
  "zellepay.com",
  "westernunion.com",
  "moneygram.com",
  "xoom.com",
  "remitly.com",
  "payeer.com",
  "adyen.com",
  "worldpay.com",
  "authorize.net",
  "braintreegateway.com",
  "checkout.com",
  "alipay.com",
  "tenpay.com",
  "unionpay.com",
  "paypay.ne.jp",
  "mercadopago.com",
  // Crypto exchanges and the wallets phishers copy.
  "binance.com",
  "binance.us",
  "binance.org",
  "coinbase.com",
  "kraken.com",
  "crypto.com",
  "gemini.com",
  "kucoin.com",
  "bybit.com",
  "okx.com",
  "bitfinex.com",
  "bitstamp.net",
  "htx.com",
  "huobi.com",
  "gate.io",
  "mexc.com",
  "bitget.com",
  "blockchain.com",
  "uphold.com",
  "cex.io",
  "bitflyer.com",
  "coincheck.com",
  "bitpanda.com",
  "nexo.com",
  "metamask.io",
  "phantom.app",
  "ledger.com",
  "trezor.io",
  "robinhood.com",
  "ftx.com",
  // Mail and the sign-in hosts of PayPal's usual neighbors.
  // Google, Microsoft, and Apple homepages stay open. Their sign-in hosts do not.
  "gmail.com",
  "googlemail.com",
  "accounts.google.com",
  "myaccount.google.com",
  "mail.google.com",
  "pay.google.com",
  "wallet.google.com",
  "docs.google.com",
  "drive.google.com",
  "calendar.google.com",
  "one.google.com",
  "admin.google.com",
  "outlook.com",
  "hotmail.com",
  "live.com",
  "office.com",
  "office365.com",
  "microsoftonline.com",
  "sharepoint.com",
  "login.microsoft.com",
  "account.microsoft.com",
  "signup.microsoft.com",
  "icloud.com",
  "me.com",
  "appleid.apple.com",
  "iforgot.apple.com",
  "idmsa.apple.com",
  "account.apple.com",
  "proton.me",
  "protonmail.com",
  "pm.me",
  "aol.com",
  "fastmail.com",
  "gmx.com",
  "gmx.net",
  "mail.com",
  "mail.yahoo.com",
  "login.yahoo.com",
  "mail.zoho.com",
  "accounts.zoho.com",
  "mail.yandex.com",
  "mail.yandex.ru",
  "passport.yandex.com",
  "passport.yandex.ru",
  "mail.qq.com",
  "mail.163.com",
  "126.com",
  "yeah.net",
  "pay.weixin.qq.com",
];

/** Real government endings. A two-letter name alone is not enough. */
const GOV: readonly string[] = [
  "gov",
  "mil",
  "fed.us",
  "gov.uk",
  "gov.scot",
  "gov.wales",
  "gov.au",
  "gov.cn",
  "gov.br",
  "gov.in",
  "gov.sg",
  "gov.hk",
  "gov.tw",
  "gov.nz",
  "govt.nz",
  "gov.za",
  "gov.ng",
  "gov.ph",
  "gov.my",
  "gov.vn",
  "gov.it",
  "gov.pl",
  "gov.il",
  "gov.ie",
  "gov.tr",
  "gov.ae",
  "gov.sa",
  "gov.eg",
  "gov.pk",
  "gov.bd",
  "gov.lk",
  "gov.ua",
  "gov.ru",
  "gouv.fr",
  "gouv.be",
  "gouv.qc.ca",
  "gc.ca",
  "canada.ca",
  "fgov.be",
  "admin.ch",
  "gob.mx",
  "gob.ar",
  "gob.cl",
  "gob.pe",
  "gob.es",
  "gob.bo",
  "gob.ve",
  "gob.ec",
  "gob.gt",
  "gob.sv",
  "gob.hn",
  "gob.ni",
  "gob.pa",
  "gob.do",
  "gob.py",
  "gob.uy",
  "gob.cu",
  "gob.cr",
  "gob.co",
  "go.jp",
  "go.kr",
  "go.id",
  "go.th",
  "go.tz",
  "go.ke",
  "go.ug",
  "go.cr",
];

function under(host: string, suffix: string): boolean {
  return host === suffix || host.endsWith(`.${suffix}`);
}

function isGov(host: string): boolean {
  for (const suffix of GOV) if (under(host, suffix)) return true;
  return false;
}

function isCreditUnion(host: string): boolean {
  if (host.includes("creditunion") || host.includes("credit-union")) return true;
  const parts = host.split(".");
  if (parts.length < 2) return false;
  const tld = parts[parts.length - 1];
  const sld = parts[parts.length - 2];
  return (tld === "org" || tld === "com") && sld.endsWith("fcu") && sld.length >= 3;
}

/** A public hostname, already lowercased, or "" when it is not a hostname. */
export function normalizeHost(host: string): string {
  return host.trim().toLowerCase().replace(/\.+$/, "").replace(/^\[|\]$/g, "");
}

export function riskyHost(host: string): boolean {
  const name = normalizeHost(host);
  if (!name || name.includes("/") || name.includes(" ") || name.includes("..")) return false;
  if (isGov(name) || isCreditUnion(name) || under(name, "bank") || under(name, "creditunion")) return true;
  for (const suffix of SUFFIX) if (under(name, suffix)) return true;
  return false;
}

/** True when a player-typed or proxied address points at a blocked host. */
export function riskyTarget(raw: string): boolean {
  const trimmed = raw.trim();
  if (!trimmed) return false;
  try {
    const abs = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    return riskyHost(new URL(abs).hostname);
  } catch {
    return false;
  }
}

/**
 * A /p/ request opened as its own page. The game iframe and its scripts are not this.
 * A crawler that omits fetch metadata is treated as a direct visit, so it never receives the other site.
 */
export function isDirectVisit(headers: { get(name: string): string | null }): boolean {
  const dest = (headers.get("sec-fetch-dest") || "").toLowerCase();
  const mode = (headers.get("sec-fetch-mode") || "").toLowerCase();
  if (dest === "iframe" || dest === "frame" || dest === "embed" || dest === "object") return false;
  if (dest && dest !== "document") return false;
  if (!dest && (mode === "cors" || mode === "no-cors" || mode === "same-origin" || mode === "websocket")) return false;
  return true;
}
